import { createContext, useContext } from "react";

import type { FormatStrings } from "@skilitics/localization";

import type { NodeSelectionCriteria } from "../../library";
import type { NodeRole } from "../../state";
import type { DesignerNode, DesignerNodeHandle } from "../../types";

export type SlotRef = {
  readonly name: string;
  readonly index?: number;
};

export type ContentRef = { readonly contentId: number; readonly title?: string } | { readonly slotRef: SlotRef };

export interface LayoutContentContext {
  readonly snapshotId: number;
  readonly layoutableId: number;
  readonly role?: NodeRole;
  readonly items: ReadonlyArray<DesignerNodeHandle>;
  readonly onAddItem: (selector: { typeId: string; definitionId?: string }) => ContentRef;
  readonly onRemoveItem: (id: number) => void;
  readonly canRemoveItem: (id: number) => boolean;
  readonly canAddItem: (
    candidate: Readonly<Pick<DesignerNode, "typeId" | "role">>,
  ) => { value: true } | { value: false; reason: FormatStrings };
  // Whether a layout.slot bound to the named slot ref may be added to this layoutable's layout.
  // Undefined blocks importing any slot-ref-bound slot.
  readonly canBindSlotRef?: (
    slotRef: SlotRef,
    context: { alreadyBoundSlotRefs: readonly SlotRef[] },
  ) => { value: true } | { value: false; reason: FormatStrings };
  // Root of this layoutable's layout subtree; scanned for alreadyBoundSlotRefs.
  readonly layoutRootId?: number;
  readonly onImportItem: (node: DesignerNode) => ContentRef;
  // Called once per distinct slot ref bound by a layout.slot in an imported subtree, after
  // canBindSlotRef has passed, so the layoutable can materialise the content backing the ref.
  readonly onImportSlotRef?: (slotRef: SlotRef) => void;
  readonly nodeSelectionCriteria: Record<"composable" | "content", NodeSelectionCriteria>;
}

export const LayoutContentContext = createContext<LayoutContentContext>({} as LayoutContentContext);

export const useLayoutConnection = () => {
  const context = useContext(LayoutContentContext);
  if (!context) {
    throw new Error("LayoutConnectionContext not found");
  }
  return context;
};
